/** Focus metadata shared by portal dialogs and their notifications. */
export interface NotificationFocus {
  opener: HTMLElement | null;
  scope: HTMLElement | null;
  dialogId?: string;
}

export function captureNotificationFocus(): NotificationFocus {
  const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const scope = opener?.closest<HTMLElement>("[data-dialog-id]")
    ?? opener?.closest<HTMLElement>('main,[role="main"]')
    ?? document.querySelector<HTMLElement>("main,[role=main]");
  return { opener, scope, dialogId: scope?.dataset.dialogId };
}

export function restoreNotificationFocus(context: NotificationFocus) {
  const currentDialog = Array.from(document.querySelectorAll<HTMLElement>("[data-dialog-id]")).at(-1);
  if (currentDialog && currentDialog !== context.scope && !currentDialog.contains(context.opener)) {
    (focusableElements(currentDialog)[0] ?? currentDialog).focus();
    return;
  }
  if (context.opener?.isConnected) {
    context.opener.focus();
    return;
  }
  const scope = context.scope?.isConnected ? context.scope : document.querySelector<HTMLElement>("[data-dialog-id],main,[role=main]");
  if (!scope) return;
  const previous = scope.getAttribute("tabindex");
  if (previous === null) {
    scope.tabIndex = -1;
    scope.addEventListener("blur", () => scope.removeAttribute("tabindex"), { once: true });
  }
  scope.focus();
}

export function focusableElements(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>('a[href],button,input,textarea,select,[contenteditable="true"],[tabindex]'))
    .filter((element) => {
      if (element.tabIndex < 0 || element.matches(':disabled,input[type="hidden"]')) return false;
      for (let ancestor: HTMLElement | null = element; ancestor; ancestor = ancestor.parentElement) {
        if (ancestor.hidden || ancestor.hasAttribute("inert") || ancestor.getAttribute("aria-hidden") === "true") return false;
        const style = getComputedStyle(ancestor);
        if (style.display === "none" || style.visibility === "hidden") return false;
      }
      return true;
    });
}
